import { RuntimeHostSettings } from './environment.types';

// -----------------------------------------------------------------------------------------------------
// @ Storage keys — each "bucket" holds one logical slice of runtime state.
//   Splitting across multiple keys means the server can inject only what it controls
//   (e.g. app-settings) without accidentally overwriting user data.
// -----------------------------------------------------------------------------------------------------

const STORAGE_KEY_SETTINGS  = 'app-settings';   // url, app, support
const STORAGE_KEY_THEME     = 'app-theme';       // logo, theme, watermark, favicon
const STORAGE_KEY_USER      = 'app-user';        // user profile, roles, access, organizations
const STORAGE_KEY_SELECTION = 'app-selection';   // selected organization + branch

// -----------------------------------------------------------------------------------------------------
// @ Helpers
// -----------------------------------------------------------------------------------------------------

/** Safely parse a localStorage bucket. Returns {} on missing/corrupt data. */
function readStorageBucket(key: string): Record<string, any> {
    if (typeof localStorage === 'undefined') {
        return {};
    }

    const raw = localStorage.getItem(key);
    if (!raw) {
        return {};
    }

    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
        localStorage.removeItem(key);
        return {};
    }
}

/** Returns `value` if it is a plain object, otherwise returns `{}`. */
function asObject(value: unknown): Record<string, any> {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, any>
        : {};
}

// -----------------------------------------------------------------------------------------------------
// @ Read
// -----------------------------------------------------------------------------------------------------

/**
 * Reads runtime settings from all sources and merges them into a single object.
 *
 * Priority (highest → lowest):
 *   1. localStorage buckets  — written after login / org selection
 *   2. window globals        — injected by the server in the HTML page
 *   3. Defaults in environment.factory.ts FALLBACKS
 *
 * Both the namespaced form (settings.url, userState.profile, …) and the legacy flat form
 * (url, user, …) are read so that older server versions still work correctly.
 */
/** Read the current window globals (injected by the server or written by a previous session). */
function readWindowGlobals(): RuntimeHostSettings {
    if (typeof window === 'undefined') {
        return {};
    }

    // window.settings is the legacy catch-all object some server versions inject.
    const legacy = asObject((window as any).settings);

    return {
        ...legacy,
        url:           window.url           ?? legacy.url,
        layout:        window.layout        ?? legacy.layout,
        support:       window.support       ?? legacy.support,
        app:           window.app           ?? legacy.app,
        organizations: window.organizations ?? legacy.organizations,
        user:          window.user          ?? legacy.user,
        access:        window.access        ?? legacy.access,
        roles:         window.roles         ?? legacy.roles,
        selection:     window.selection     ?? legacy.selection
    };
}

export function readRuntimeSettings(): RuntimeHostSettings {
    const win   = readWindowGlobals();
    const sB    = readStorageBucket(STORAGE_KEY_SETTINGS);
    const tB    = readStorageBucket(STORAGE_KEY_THEME);
    const uB    = readStorageBucket(STORAGE_KEY_USER);
    const selB  = readStorageBucket(STORAGE_KEY_SELECTION);

    // -- Static config (url / app / support) -------------------------------------------------------
    // localStorage wins over window globals. Namespaced key wins over flat key within each source.
    const settings = {
        url: {
            ...asObject(win.url),
            ...asObject(win.settings?.url),
            ...asObject(sB.url),
            ...asObject(sB.settings?.url)
        },
        app: {
            ...asObject(win.app),
            ...asObject(win.settings?.app),
            ...asObject(sB.app),
            ...asObject(sB.settings?.app)
        },
        support: {
            ...asObject(win.support),
            ...asObject(win.settings?.support),
            ...asObject(sB.support),
            ...asObject(sB.settings?.support)
        }
    };

    // -- Theme / layout ----------------------------------------------------------------------------
    const theme = {
        ...asObject(win.layout),
        ...asObject(win.theme),
        ...asObject(tB.layout),
        ...asObject(tB.theme)
    };

    // -- User state --------------------------------------------------------------------------------
    const userProfile = {
        ...asObject(win.user),
        ...asObject(win.userState?.profile),
        ...asObject(uB.user),
        ...asObject(uB.userState?.profile)
    };

    const userRoles = {
        ...asObject(win.roles),
        ...asObject(win.userState?.roles),
        ...asObject(uB.roles),
        ...asObject(uB.userState?.roles)
    };

    const userState = {
        profile:       userProfile,
        roles:         userRoles,
        access:        uB.userState?.access        ?? uB.access        ?? win.userState?.access        ?? win.access        ?? {},
        organizations: uB.userState?.organizations ?? uB.organizations ?? win.userState?.organizations ?? win.organizations ?? []
    };

    // -- Selection (active org + branch) -----------------------------------------------------------
    const selectionState = {
        // Full objects (needed to read org.code / branch.code without extra lookups)
        organization: selB.selectionState?.organization ?? selB.organization ?? (win as any).organization ?? null,
        branch:       selB.selectionState?.branch       ?? selB.branch       ?? (win as any).branch       ?? null,
        // Numeric indexes (used for array-position lookups in access map)
        indexes: {
            ...asObject(win.selection),
            ...asObject(win.selectionState?.indexes),
            ...asObject(selB.selection),
            ...asObject(selB.selectionState?.indexes)
        }
    };

    const merged: RuntimeHostSettings = {
        // Namespaced form (consumed by environment.factory.ts)
        settings,
        theme,
        userState,
        selectionState,
        // Flat aliases (consumed by auth.storage.ts and legacy readers)
        url:           settings.url,
        app:           settings.app,
        support:       settings.support,
        layout:        theme,
        user:          userProfile,
        roles:         userRoles,
        access:        userState.access,
        organizations: userState.organizations,
        selection:     selectionState.indexes
    };

    // Write merged result back to window globals so the current page session stays in sync
    // without requiring a page reload.
    writeWindowRuntimeSettings(merged);

    return merged;
}

// -----------------------------------------------------------------------------------------------------
// @ Write — called by auth.storage.ts after login / org selection so the merged state
//   is available on `window` for the current page session (no page-reload required).
// -----------------------------------------------------------------------------------------------------

export function writeWindowRuntimeSettings(settings: RuntimeHostSettings): void {
    if (typeof window === 'undefined') {
        return;
    }

    window.url           = settings.url;
    window.layout        = settings.layout;
    window.support       = settings.support;
    window.app           = settings.app;
    window.organizations = settings.organizations;
    window.user          = settings.user;
    window.access        = settings.access;
    window.roles         = settings.roles;
    window.selection     = settings.selection;

    // Keep the full merged object on window.settings for legacy consumers.
    (window as any).settings = settings;
}
